import jsonLogic from 'json-logic-js';

import {
    FieldCondition,
    GroupsForProduct,
    ProductRequirementGroup,
    RequirementField,
    RequirementFieldWithChildren,
} from '../data-collection.dto';

/**
 * Merge groups fetched per-product into a single list keyed by group `code`.
 * The backend catalog shares groups across products, so the wire `ProductRequirementGroup`
 * no longer carries a `productId` — provenance must come from the caller (which
 * product UUID was used to fetch each batch).
 *
 * When two products contribute the same group code, we keep the first
 * occurrence's name and the lower `displayOrder` (so the tab appears as early
 * as any of the source products want it), then union their fields (deduped by
 * `code`, preserving first occurrence). Each merged field carries
 * `productIds: string[]` — the set of products that included it — so the save
 * path can fan out to every owning service. The final list is sorted by
 * displayOrder.
 */
export function mergeGroupsByCode(perProduct: GroupsForProduct[]): ProductRequirementGroup[] {
    const merged = new Map<string, ProductRequirementGroup>();
    for (const { productId, groups } of perProduct) {
        for (const group of groups) {
            const existing = merged.get(group.code);
            if (!existing) {
                merged.set(group.code, {
                    ...group,
                    requirementFields: group.requirementFields.map((f) => ({
                        ...f,
                        productIds: [productId],
                    })),
                });
                continue;
            }
            if (group.displayOrder < existing.displayOrder) {
                existing.displayOrder = group.displayOrder;
            }
            const byCode = new Map(existing.requirementFields.map((f) => [f.code, f] as const));
            for (const field of group.requirementFields) {
                const seen = byCode.get(field.code);
                if (seen) {
                    if (!seen.productIds?.includes(productId)) {
                        seen.productIds = [...(seen.productIds ?? []), productId];
                    }
                } else {
                    const next = { ...field, productIds: [productId] };
                    existing.requirementFields.push(next);
                    byCode.set(next.code, next);
                }
            }
        }
    }
    for (const group of merged.values()) {
        group.requirementFields.sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return [...merged.values()].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Overlay the per-product groups onto the full requirement catalog (GEN-653).
 *
 * Every catalog group/field always appears. Fields also present in a connected
 * product's fetch keep that copy — it carries the per-product
 * `dataCriticality` / `criticalityWhen` and `productIds` provenance. Fields
 * only in the catalog get `productIds: []` (editable, neutral criticality).
 * Per-product groups missing from the catalog are kept defensively. Groups and
 * fields sort by the catalog's `displayOrder`.
 */
export function mergeCatalogWithProductGroups(
    catalog: ProductRequirementGroup[],
    perProduct: GroupsForProduct[],
): ProductRequirementGroup[] {
    const merged = new Map(mergeGroupsByCode(perProduct).map((g) => [g.code, g] as const));
    for (const group of catalog) {
        const existing = merged.get(group.code);
        if (!existing) {
            merged.set(group.code, {
                ...group,
                requirementFields: group.requirementFields.map((f) => ({ ...f, productIds: [] })),
            });
            continue;
        }
        // The catalog is canonical for group identity, ordering and placement;
        // the per-product merge kept the first-seen name and the lowest
        // per-product order, which may disagree.
        existing.id = group.id;
        existing.name = group.name;
        existing.displayOrder = group.displayOrder;
        existing.placement = group.placement;
        const byCode = new Set(existing.requirementFields.map((f) => f.code));
        for (const field of group.requirementFields) {
            if (!byCode.has(field.code)) {
                existing.requirementFields.push({ ...field, productIds: [] });
            }
        }
    }
    for (const group of merged.values()) {
        group.requirementFields.sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return [...merged.values()].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Build the top-level fields with their resolved children attached. Backend
 * sends a flat list of `RequirementField` rows; LIST sub-fields point to their
 * parent via `parentFieldId`. This produces a forest of top-level fields, each
 * with its `subFields` populated.
 */
export function buildFieldTree(fields: RequirementField[]): RequirementFieldWithChildren[] {
    const byId = new Map<string, RequirementFieldWithChildren>();
    for (const f of fields) {
        byId.set(f.id, { ...f, subFields: [] });
    }
    const roots: RequirementFieldWithChildren[] = [];
    for (const f of byId.values()) {
        if (f.parentFieldId && byId.has(f.parentFieldId)) {
            byId.get(f.parentFieldId)!.subFields.push(f);
        } else if (!f.parentFieldId) {
            roots.push(f);
        }
    }
    for (const f of byId.values()) {
        f.subFields.sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return roots.sort((a, b) => a.displayOrder - b.displayOrder);
}

// ── Condition evaluator (json-logic) ─────────────────────────────────────
//
// Wire format and operator semantics are documented in
// `dev-context/conventions/condition-dsl.md`. Custom operators must match the
// backend's allow-list in `FieldConditionCodec`.

let customOpsRegistered = false;

function registerCustomOps() {
    if (customOpsRegistered) return;
    customOpsRegistered = true;

    // Stock json-logic doesn't ship our concept of "value is set" (not null,
    // not undefined, not the empty string). `exists` / `not_exists` cover that
    // explicitly to keep the semantics under our control.
    jsonLogic.add_operation(
        'exists',
        (value: unknown): boolean => value !== null && value !== undefined && value !== '',
    );
    jsonLogic.add_operation(
        'not_exists',
        (value: unknown): boolean => value === null || value === undefined || value === '',
    );

    // count_* operates on the length of an array-valued field (LIST entries).
    // Non-array actuals evaluate to false rather than throwing — keeps the
    // evaluator total over malformed values.
    const countOp =
        (cmp: (n: number, e: number) => boolean) =>
        (actual: unknown, expected: unknown): boolean =>
            Array.isArray(actual) && typeof expected === 'number' && cmp(actual.length, expected);

    jsonLogic.add_operation(
        'count_gt',
        countOp((n, e) => n > e),
    );
    jsonLogic.add_operation(
        'count_gte',
        countOp((n, e) => n >= e),
    );
    jsonLogic.add_operation(
        'count_eq',
        countOp((n, e) => n === e),
    );
    jsonLogic.add_operation(
        'count_lt',
        countOp((n, e) => n < e),
    );
    jsonLogic.add_operation(
        'count_lte',
        countOp((n, e) => n <= e),
    );
}

registerCustomOps();

/** Lookup keyed by field code for fast condition evaluation. */
export type FieldValueMap = Record<string, unknown>;

export function isFieldVisible(field: RequirementField, values: FieldValueMap): boolean {
    return field.visibleWhen == null ? true : evaluate(field.visibleWhen, values);
}

export function isFieldRequired(field: RequirementField, values: FieldValueMap): boolean {
    if (field.required) return true;
    return field.requiredWhen == null ? false : evaluate(field.requiredWhen, values);
}

function evaluate(rule: FieldCondition, values: FieldValueMap): boolean {
    // Fail-closed: any well-formed boolean rule returns `true` or `false`.
    // json-logic-js silently passes the input through when it isn't recognized
    // as a logic expression (`is_logic()` is false — e.g. a multi-key object
    // from a typo), and `Boolean({...})` would be truthy. Requiring `=== true`
    // means malformed rules treat the field as hidden / not required, which is
    // the safe direction.
    try {
        return jsonLogic.apply(rule, values) === true;
    } catch {
        return false;
    }
}

/** Max number of LIST rows the renderer will materialize from a list_size expression. */
export const MAX_LIST_SIZE = 100;

/**
 * Evaluate a `list_size` expression and return a row count, or `null` when the
 * expression doesn't yet resolve to a usable number (e.g. its referenced
 * sibling is still empty). Non-integer results are floored; negatives clamp to
 * 0; anything above [[MAX_LIST_SIZE]] clamps down — defense against a runaway
 * expression rendering thousands of rows.
 *
 * Numeric strings (e.g. "3" from a SELECT field whose options are bare
 * numbers) are accepted: `SelectFieldInput.onChange` forces every selected
 * value through `String(...)` before storing on the form state, so a
 * `{"var": "meter_count"}` that points at a numeric SELECT resolves to "3"
 * rather than 3. Coerce here rather than asking every seed to wrap its
 * `list_size` reference in a `to_number` op.
 */
export function evaluateListSize(
    rule: FieldCondition,
    values: FieldValueMap,
    max: number = MAX_LIST_SIZE,
): number | null {
    try {
        const raw = jsonLogic.apply(rule, values);
        const n = coerceToFiniteNumber(raw);
        if (n === null) return null;
        return Math.min(Math.max(Math.floor(n), 0), max);
    } catch {
        return null;
    }
}

/**
 * Accepts numbers and numeric strings; rejects everything else (null,
 * undefined, empty string, NaN, non-numeric strings, booleans, objects, …).
 * The empty-string check is what `Number(...)` alone won't give us — it
 * coerces "" to 0, which would silently turn an unfilled gate into a
 * zero-row list (indistinguishable from "no answer yet").
 */
function coerceToFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
